import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * Step 2 of the SPA mass renewal: the AS400 agreements and item-master
 * exports → the supplier's current load lines with their margin, cost adder
 * and scope worked out, and the supplier's active items.
 *
 * Replaces the Alteryx "AS400 current agreements" and "active items" regions
 * (the batch macros, the vendor filter, the LOAD STATUS / LOADED TO /
 * MarginCalc / CostAddCalc formulas).
 */
export const spaRenewalReadAs400LoadsStep = defineStepConfig({
  type: "spa_renewal_read_as400_loads",
  name: "SPA Renewal: Read AS400 Loads",
  description:
    "Streams the AS400 agreements export (AGREE_* daily drop or a named-header extract) and the item master " +
    "(PRODSUM*), keeps the supplier's SPA load lines and active items, and computes each line's status, scope " +
    "(group / class / territory / account), margin and cost adder. The exports are read row by row, so a " +
    "multi-million-row drop never has to fit in memory.",
  category: "Transformation",
  libraryModule: "Pricing",

  inputSchema: z.object({
    agreementDocuments: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .optional()
      .describe(
        "AS400 agreements exports, one or more (e.g. one per division). " +
          "Leave empty to read the Data Asset chosen in the step config.",
      ),
    itemDocuments: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .optional()
      .describe(
        "AS400 item master exports, one or more (e.g. one per division). " +
          "Leave empty to read the Data Asset chosen in the step config.",
      ),
  }),

  outputSchema: z.object({
    loadsDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "CSV of the supplier's current and future load lines, with LOAD STATUS, LOADED TO, MarginCalc and CostAddCalc.",
    ),
    itemsDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "CSV of the supplier's active items with their price columns, pricing factor, supplier item and keyword.",
    ),
    loadLineCount: z.number().int(),
    itemCount: z.number().int(),
  }),

  configSchema: z.object({
    agreementsDataAssetId: z
      .string()
      .optional()
      .describe(
        "Data Asset holding the AS400 agreements export, read when no agreements document is wired in or uploaded.",
      ),
    itemsDataAssetId: z
      .string()
      .optional()
      .describe(
        "Data Asset holding the AS400 item master export, read when no item document is wired in or uploaded.",
      ),
    vendorItemPrefixes: z
      .array(z.string().min(1))
      .min(1)
      .default(["HUB", "BUR", "KIL"])
      .describe(
        "AS400 product-number prefixes of the supplier's items (Hubbell: HUB, BUR, KIL).",
      ),
    excludeLoadTypes: z
      .array(z.string())
      .default(["E"])
      .describe("Load types to leave out (E = exception loads)."),
    excludePoContaining: z
      .array(z.string())
      .default(["LANDED", "M-", "N-"])
      .describe("Loads whose PO contains any of these are not supplier SPAs."),
    spaNumberLength: z
      .number()
      .int()
      .positive()
      .default(8)
      .describe("The SPA number is the first N characters of the cleaned PO."),
    spaNumberPrefixes: z
      .array(z.string())
      .default([])
      .describe(
        "Only keep SPA numbers starting with one of these (e.g. ['3']). Empty keeps all.",
      ),
    asOfDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .describe(
        "Loads starting after this date are FUTURE and left out of the renewal. Defaults to today.",
      ),
  }),

  execution: {
    // Streaming a 600 MB daily drop takes minutes, not seconds.
    startToCloseTimeout: "30 minutes",
  },

  emits: [] as const,
});
